import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useApp } from "../components/appState";
import { Flyer } from "../components/Flyer";
import { Icon } from "../components/Icons";
import { pastShot, reelFrom, showShot, wallReel } from "../lib/flyerViewer";
import { dayOfMonth, dow, fLong, MON, day, plural } from "../lib/format";
import { SCOPES, byShow, fetchMedia, filterMedia, hostOf, thumbUrl, type MediaLink, type MediaScope } from "../lib/mediaPage";
import { logValue } from "../lib/sheets";
import { mediaTypeLabel } from "../lib/showSheet";
import {
  MORE,
  PAGE,
  applyFilters,
  dismissWent,
  fetchShows,
  filtersFromParams,
  groupByYear,
  isFiltering,
  wallTiles,
  yearsAgo,
  type ShowFilters,
} from "../lib/showsPage";
import type { ShowRow as Row, ShowsDto } from "../lib/types";

const flyerText = (s: Row) => ({ date: s.date, bands: s.bill.map((b) => b.name), venue: s.venue });

/** Loads the next page when it scrolls into view, so the list and the Wall need no button press. */
function AutoMore({ onMore }: { onMore: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && onMore(), { rootMargin: "600px" });
    io.observe(el);
    return () => io.disconnect();
  }, [onMore]);
  return <div ref={ref} aria-hidden="true" />;
}

/** Every link across shows, grouped under its show (Matt's pick: a view on Shows, 2026-09-11). Editing stays on the show sheet. */
function MediaView({ q, year, scope, audio }: { q: string; year: number | null; scope: MediaScope | null; audio: boolean }) {
  const { open, version } = useApp();
  const [items, setItems] = useState<MediaLink[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => {
    let live = true;
    fetchMedia()
      .then((x) => live && setItems(x))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [version]);
  useEffect(() => setLimit(PAGE), [q, year, scope, audio]);
  const more = useCallback(() => setLimit((l) => l + MORE), []);
  if (error) return <p className="error">{error}</p>;
  if (!items) return <p className="loading">Loading media…</p>;
  const hits = filterMedia(items, { q, year, scope, audio });
  const shows = byShow(hits);
  const filtering = Boolean(q.trim() || year || scope || audio);
  return (
    <div className="media-list">
      <p className="meta mono">
        {filtering ? `${hits.length} of ${plural(items.length, "link")}` : `${plural(items.length, "link")} from ${plural(shows.length, "show")}`}
      </p>
      {shows.length ? (
        shows.slice(0, limit).map((s) => (
          <section className="mshow" key={s.show_id}>
            <button type="button" className="msh" onClick={() => open("show", s.show_id)}>
              <span className="d mono">{fLong(s.date)}</span>
              <span className="v">{s.venue}</span>
              {s.headliner && <span className="h">{s.headliner}</span>}
              {s.event && <span className="pill">{s.event}</span>}
            </button>
            {s.links.map((m) => {
              const thumb = thumbUrl(m.url);
              return (
                <a key={m.id} className="mlink" href={m.url} target="_blank" rel="noopener noreferrer">
                  <span className={`mthumb ${m.media_format}`}>
                    {thumb && <img src={thumb} alt="" loading="lazy" decoding="async" onError={(e) => (e.currentTarget.hidden = true)} />}
                    <span className="host">{hostOf(m.url)}</span>
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span className="mt">{m.title || m.band || mediaTypeLabel(m.media_type)}</span>
                    <span className="ms">{[m.band ?? "Whole show", mediaTypeLabel(m.media_type), m.media_format === "audio" ? "audio" : null].filter(Boolean).join(" · ")}</span>
                    {m.notes && <span className="mn">{m.notes}</span>}
                  </span>
                </a>
              );
            })}
          </section>
        ))
      ) : (
        <p className="muted">No links match. Try fewer words, or clear the chips.</p>
      )}
      {shows.length > limit && <AutoMore onMore={more} />}
      {shows.length > limit && (
        <button type="button" className="btn ghost more" onClick={more}>
          Show more ({shows.length - limit} shows)
        </button>
      )}
    </div>
  );
}

export function ShowRow({ show, reel }: { show: Row; reel?: Row[] }) {
  const { open, zoom } = useApp();
  return (
    <div className="srow">
      <div className="thumb">
        <Flyer url={show.image_url} alt={`Flyer for show #${show.number}`} text={flyerText(show)}
          onZoom={() => zoom(reelFrom(reel ?? [show], show, (r) => showShot(r)))} />
      </div>
      <button type="button" className="srow-face" onClick={() => open("show", show.id)}>
        <div style={{ minWidth: 0 }}>
          <div className="snum mono">
            <b>#{show.number}</b> · {fLong(show.date)}
          </div>
          <div className="bill">
            {show.bill.map((b, i) => (
              <span key={b.band_id}>
                {i > 0 && ", "}
                {i === 0 ? <b>{b.name}</b> : b.name}
              </span>
            ))}
          </div>
          <div className="sv">
            {show.venue}
            {show.event && (
              <>
                {" · "}
                <span className="ev">{show.event}</span>
              </>
            )}
          </div>
        </div>
        <div className="sx">
          {show.media_count > 0 && <span className="mg">▶ {show.media_count}</span>}
        </div>
      </button>
    </div>
  );
}

function TodayStrip() {
  const { todayInfo, today, open, zoom, changed, toast } = useApp();
  if (!todayInfo) return null;
  const { went, on_this_day } = todayInfo;
  const d = day(today);
  return (
    <>
      {went.map((w) => (
        <div className="went" key={w.id}>
          <div className="ud">
            <span className="dow">{dow(w.date)}</span>
            <span className="day">{dayOfMonth(w.date)}</span>
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="q">Went?</div>
            <div className="t">{w.event_name}</div>
            <div className="sv">
              {w.venue} · {fLong(w.date)} · you said {w.rsvp === "yes" ? "Going" : "Maybe"}
            </div>
            <div className="acts">
              <button type="button" className="btn primary sm" onClick={() => open("log", logValue(w.id))}>
                Log it
              </button>
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => dismissWent(w.id).then(() => changed("Cleared"), (e: Error) => toast(e.message))}
              >
                Didn't go
              </button>
            </div>
          </div>
        </div>
      ))}
      {on_this_day.length > 0 && (
        <section>
          <div className="sect-h">
            <p className="eyebrow">
              On this day · {MON[d.getMonth()]} {d.getDate()}
            </p>
            <span className="meta">{plural(on_this_day.length, "show")}</span>
          </div>
          <div className="otd">
            {on_this_day.map((s) => (
              <div className="otd-t" key={s.id}>
                <Flyer url={s.image_url} alt={`Flyer for ${s.bands[0] ?? s.venue}`}
                  text={{ date: s.date, bands: s.bands, venue: s.venue }}
                  onZoom={() => zoom(reelFrom(on_this_day, s, pastShot))} />
                <button type="button" className="ago" onClick={() => open("show", s.id)}>
                  <b>{plural(yearsAgo(s.date, today), "year")} ago</b>
                  <br />
                  {s.bands[0]}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

export default function Shows() {
  const { zoom, version } = useApp();
  const [sp, setSp] = useSearchParams();
  const filters = filtersFromParams(sp);
  const view = sp.get("view") === "wall" ? "wall" : sp.get("view") === "media" ? "media" : "log";
  const wall = view === "wall";
  const scope = (SCOPES.find((c) => c.value === sp.get("scope"))?.value ?? null) as MediaScope | null;
  const audio = sp.get("audio") === "1";
  const setParam = (k: string, v: string | null) => setSp((p) => (v ? p.set(k, v) : p.delete(k), p), { replace: true });
  const [q, setQ] = useState(filters.q);
  const [data, setData] = useState<ShowsDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(PAGE);

  const set = (f: Partial<ShowFilters>) => setSp((prev) => applyFilters(prev, { ...filtersFromParams(prev), ...f }), { replace: true });

  // Search as you type, once typing pauses.
  useEffect(() => {
    if (q === filters.q) return;
    const t = setTimeout(() => set({ q }), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  useEffect(() => {
    let live = true;
    setError(null);
    fetchShows(filters)
      .then((d) => {
        if (!live) return;
        setData(d);
        setLimit(PAGE);
      })
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q, filters.year, version]);

  const more = useCallback(() => setLimit((l) => l + MORE), []);
  const filtering = isFiltering(filters);
  const shows = data?.shows ?? [];
  const tiles = wall ? wallTiles(shows) : [];
  // The Media view pages its own list.
  const count = view === "media" ? 0 : wall ? tiles.length : shows.length;
  const shown = shows.slice(0, limit);
  // The reel is what the Wall has actually laid out, so the arrows walk what
  // you can see rather than every flyer the page has fetched.
  const onWall = tiles.slice(0, limit);
  const remaining = count - Math.min(limit, count);
  const firstYear = data?.years.length ? data.years[data.years.length - 1].year : null;

  return (
    <>
      <div className="ph">
        <h1>Shows</h1>
        {data && (
          <span className="meta mono">
            {filtering ? `${shows.length} of ${data.total}` : `${data.total}${firstYear ? ` since ${firstYear}` : ""}`}
          </span>
        )}
      </div>
      {!filtering && <TodayStrip />}
      <div className="toolbar">
        <label className="search">
          <Icon name="search" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Bands, venues, events" aria-label="Search shows" />
        </label>
        <div className="row-between">
          {view === "media" ? (
            <div className="chips" role="group" aria-label="Kind of link">
              {SCOPES.map((c) => (
                <button type="button" key={c.value} aria-pressed={scope === c.value} onClick={() => setParam("scope", scope === c.value ? null : c.value)}>
                  {c.label}
                </button>
              ))}
              <button type="button" aria-pressed={audio} onClick={() => setParam("audio", audio ? null : "1")}>
                Audio
              </button>
            </div>
          ) : (
            <div />
          )}
          <div className="seg">
            <button type="button" aria-pressed={view === "log"} onClick={() => setParam("view", null)}>
              Log
            </button>
            <button type="button" aria-pressed={wall} onClick={() => setParam("view", "wall")}>
              Wall
            </button>
            <button type="button" aria-pressed={view === "media"} onClick={() => setParam("view", "media")}>
              Media
            </button>
          </div>
        </div>
        {data && (
          <div className="years" role="group" aria-label="Year">
            {data.years.map((y) => (
              <button type="button" key={y.year} aria-pressed={filters.year === y.year} onClick={() => set({ year: filters.year === y.year ? null : y.year })}>
                <span className="y">'{String(y.year).slice(2)}</span>
                <span className="c">{y.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {error && <p className="error">{error}</p>}
      {!data && !error && <p className="loading">Loading shows…</p>}
      {view === "media" && <MediaView q={filters.q} year={filters.year} scope={scope} audio={audio} />}
      {data && view !== "media" && (
        <div>
          {wall ? (
            <div className="wall">
              {onWall.map((tile) => (
                <div className={`wtile${tile.shows.length > 1 ? " stack" : ""}`} key={tile.url}>
                  <Flyer url={tile.url}
                    alt={tile.shows.length > 1 ? `Flyer shared by ${tile.shows.length} shows` : `Flyer for show #${tile.shows[0].number}`}
                    text={flyerText(tile.shows[0])} onZoom={() => zoom(wallReel(onWall, tile))} />
                  {tile.shows.length > 1 && <span className="count">{tile.shows.length} shows</span>}
                </div>
              ))}
            </div>
          ) : shown.length ? (
            groupByYear(shown, data.years).map((g) => (
              <div key={g.year}>
                <div className="yh">
                  <b>{g.year}</b>
                  <span>{plural(g.total, "show")}</span>
                </div>
                {g.shows.map((s) => (
                  <ShowRow key={s.id} show={s} reel={shown} />
                ))}
              </div>
            ))
          ) : (
            <p className="muted">No shows match. Try fewer words, or clear the year.</p>
          )}
        </div>
      )}
      {remaining > 0 && <AutoMore onMore={more} />}
      {remaining > 0 && (
        <button type="button" className="btn ghost more" onClick={more}>
          Show {Math.min(MORE, remaining)} more of {remaining}
        </button>
      )}
    </>
  );
}
